/**
 * Home: scan the machine (or type its code). Nothing else is chosen here: as in
 * the Android app, the customer next selects a column ON THE MACHINE, and only
 * then chooses how to pay for the price the machine reports (ADR-0026).
 */
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ActionKeys, NetworkError, errorKey, getVisit, openVisit } from "../api";
import { AppBar, ErrorCard, Spinner } from "../components";
import { useI18n } from "../i18n";
import { isAppEntryQr, machineCodeFrom, machineCodeFromLocation } from "../qr";
import { QrScanner } from "../scanner/QrScanner";
import { store } from "../storage";
import { reportError } from "../support/supportContext";
import { isResumable, type VendSession } from "../vend";

export function HomeScreen() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const flash = (location.state as { flash?: string } | null)?.flash ?? null;

  const [scanning, setScanning] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resume, setResume] = useState<VendSession | null>(null);
  const keys = useRef(new ActionKeys());
  const manualInput = useRef<HTMLInputElement>(null);

  // A visit still in progress (e.g. the customer left for the payment page).
  useEffect(() => {
    const saved = store.get("visit");
    if (!saved) return;
    const ctrl = new AbortController();
    getVisit(saved, ctrl.signal)
      .then((v) => {
        if (isResumable(v)) setResume(v);
        else store.remove("visit");
      })
      .catch((e) => {
        if (!(e instanceof NetworkError)) store.remove("visit");
      });
    return () => ctrl.abort();
  }, []);

  // A code in this page's own address is prefilled, never started by itself:
  // opening an order is a real action and needs the customer's tap.
  useEffect(() => {
    const code = machineCodeFromLocation(location.search);
    if (code) {
      setManual(code);
      setManualOpen(true);
    }
  }, [location.search]);

  useEffect(() => {
    if (manualOpen) manualInput.current?.focus();
  }, [manualOpen]);

  async function start(code: string) {
    setScanning(false);
    setBusy(true);
    setError(null);
    const action = `open:${code}`;
    try {
      const v = await openVisit(code, keys.current.for(action));
      keys.current.done(action);
      store.set("visit", v.vend_session_id);
      store.set("qr", code);
      navigate(`/visit/${encodeURIComponent(v.vend_session_id)}`);
    } catch (e) {
      // After a dropped connection the same tap must reuse the same key, so a
      // retry cannot open a second order; any answer from the server ends it.
      if (!(e instanceof NetworkError)) keys.current.done(action);
      reportError(errorKey(e), e);
      setError(t(errorKey(e)));
    } finally {
      setBusy(false);
    }
  }

  function submitManual() {
    const code = machineCodeFrom(manual);
    if (!code) {
      const key = isAppEntryQr(manual) ? "vend.scan.appQr" : "vend.scan.notMachine";
      reportError(key);
      setError(t(key));
      return;
    }
    void start(code);
  }

  return (
    <div className="aseed-shell">
      <AppBar />
      <main className="aseed-shell__main">
        <section className="v-hero">
          <p className="v-hero__lead">{t("vend.home.lead")}</p>
        </section>

        <div className="aseed-shell__content v-stack">
          {flash ? <div className="aseed-status-banner v-note">{t(flash)}</div> : null}
          {error ? <ErrorCard>{error}</ErrorCard> : null}

          {resume ? (
            <div className="v-card v-resume">
              <div>
                <p className="v-resume__title">{t("vend.resume.title")}</p>
                <p className="v-muted">{t(`vend.state.${resume.state}.title`)}</p>
              </div>
              <button
                type="button"
                className="aseed-button"
                onClick={() => navigate(`/visit/${encodeURIComponent(resume.vend_session_id)}`)}
              >
                {t("vend.resume.action")}
              </button>
            </div>
          ) : null}

          <button
            type="button"
            className="aseed-button v-scan-button"
            disabled={busy}
            onClick={() => {
              setError(null);
              setScanning(true);
            }}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 8V5a1 1 0 0 1 1-1h3M16 4h3a1 1 0 0 1 1 1v3M20 16v3a1 1 0 0 1-1 1h-3M8 20H5a1 1 0 0 1-1-1v-3M7 12h10" />
            </svg>
            {t("vend.scan.open")}
          </button>

          <section className="v-card">
            <button
              type="button"
              className="v-disclosure"
              aria-expanded={manualOpen}
              onClick={() => setManualOpen((o) => !o)}
            >
              {t("vend.manual.title")}
            </button>
            {manualOpen ? (
              <form
                className="v-manual"
                onSubmit={(e) => {
                  e.preventDefault();
                  submitManual();
                }}
              >
                <label className="v-muted v-small" htmlFor="v-manual-code">
                  {t("vend.manual.hint")}
                </label>
                <input
                  id="v-manual-code"
                  ref={manualInput}
                  className="v-input"
                  value={manual}
                  onChange={(e) => setManual(e.target.value)}
                  placeholder="QRC-"
                  autoCapitalize="characters"
                  autoComplete="off"
                  spellCheck={false}
                  inputMode="text"
                />
                <button type="submit" className="aseed-button" disabled={busy || !manual.trim()}>
                  {t("vend.manual.submit")}
                </button>
              </form>
            ) : null}
          </section>
        </div>
      </main>

      {busy ? (
        <div className="v-busy" role="status" aria-live="polite">
          <Spinner />
          <p>{t("vend.connecting")}</p>
        </div>
      ) : null}

      {scanning ? (
        <QrScanner
          onCode={(code) => void start(code)}
          onClose={() => setScanning(false)}
          onManual={() => {
            setScanning(false);
            setManualOpen(true);
          }}
        />
      ) : null}
    </div>
  );
}
